import { Box, useMediaQuery, useTheme } from '@mui/material';

import {
  LearningPathCardSkeleton,
  LearningPathCardVariants,
  LearningPathHorizontalCard,
  LearningPathListCard,
} from '@/components';
import { EVENT_CONSTANTS } from '@/services';
import { LearningPathWithDuration } from '@/types';

const REFERRER = {
  page: EVENT_CONSTANTS.PAGE.LEARNING_PATHS,
};

export const LearningPathsListVariants = {
  LIST: 'list',
  HORIZONTAL: 'horizontal',
} as const;

export type LearningPathsListVariant =
  (typeof LearningPathsListVariants)[keyof typeof LearningPathsListVariants];

export const LearningPathsList = ({
  learningPaths,
  variant,
  organizationName,
  isLoading = false,
  skeletonCount = 3,
}: {
  learningPaths: LearningPathWithDuration[];
  variant: LearningPathsListVariant;
  organizationName?: string;
  isLoading?: boolean;
  skeletonCount?: number;
}) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  if (!isMd || variant === LearningPathsListVariants.HORIZONTAL) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {isLoading
          ? Array.from({ length: skeletonCount }).map((_, index) => (
              <Box key={index} sx={{ flex: 1, containerType: 'inline-size' }}>
                <LearningPathCardSkeleton
                  variant={LearningPathCardVariants.HORIZONTAL}
                />
              </Box>
            ))
          : learningPaths.map(lp => (
              <Box
                key={lp.groupId}
                sx={{ flex: 1, containerType: 'inline-size' }}>
                <LearningPathHorizontalCard
                  key={lp.groupId}
                  learningPath={lp}
                  referrer={REFERRER}
                />
              </Box>
            ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {isLoading
        ? Array.from({ length: skeletonCount }).map((_, index) => (
            <LearningPathCardSkeleton
              key={index}
              variant={LearningPathCardVariants.LIST}
            />
          ))
        : learningPaths.map(lp => (
            <LearningPathListCard
              key={lp.groupId}
              learningPath={lp}
              referrer={REFERRER}
              userOrganizationName={organizationName}
            />
          ))}
    </Box>
  );
};
